import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';
import { SlotFormGroup } from '@app/shared/forms/slot-form/slot.model';
import moment from 'moment';
import { DayOfWeek, Schedule, ScheduleFormGroup } from './schedules.model';

export function noOverlapValidator(): ValidatorFn {
  return (control: AbstractControl<ScheduleFormGroup>): ValidationErrors | null => {
    const slots = control.get('slots') as unknown as FormArray<SlotFormGroup>;
    if (!slots) return null;
    const timeRanges = slots.controls
      .filter((slotControl) => {
        const start = slotControl.controls.startTime.value;
        const end = slotControl.controls.endTime.value;
        return start && end;
      })
      .sort((a, b) => {
        const startA = a.controls.startTime.value!;
        const startB = b.controls.startTime.value!;
        return startA.localeCompare(startB);
      })
      .map((slotControl) => {
        const start = slotControl.controls.startTime.value!;
        const end = slotControl.controls.endTime.value!;
        return { start, end };
      });
    for (let i = 1; i < timeRanges.length; i++) {
      if (timeRanges[i].start < timeRanges[i - 1].end) {
        return { overlap: true };
      }
    }
    return null;
  };
}

export function minTimeBetweenDay(minHours: number): ValidatorFn {
  return (control: AbstractControl<Schedule[]>): ValidationErrors | null => {
    const schedules = control.value;
    if (!schedules) return null;
    const errorDays: DayOfWeek[] = [];
    for (let i = 1; i < schedules.length; i++) {
      const errorDay = ensureMinTimeBetweenDays(schedules[i - 1], schedules[i], minHours);
      if (errorDay) {
        errorDays.push(errorDay);
      }
    }
    const firstSchedule = schedules[0];
    const lastSchedule = schedules.slice(-1)[0];
    if (firstSchedule && lastSchedule) {
      const errorDay = ensureMinTimeBetweenDays(lastSchedule, firstSchedule, minHours);
      if (errorDay) {
        errorDays.push(errorDay);
      }
    }
    if (errorDays.length > 0) {
      return { minTimeBetweenDay: errorDays };
    }
    return null;
  };
}

function ensureMinTimeBetweenDays(
  schedule1: Schedule,
  schedule2: Schedule,
  minHours: number,
): DayOfWeek | null {
  const previousDayLastSlot = schedule1.slots
    .filter((slot) => slot.startTime && slot.endTime)
    .slice(-1)[0];
  const currentDayFirstSlot = schedule2.slots.filter((slot) => slot.startTime && slot.endTime)[0];
  if (previousDayLastSlot && currentDayFirstSlot) {
    const previousEnd = moment(previousDayLastSlot.endTime, 'HH:mm');
    const currentStart = moment(currentDayFirstSlot.startTime, 'HH:mm');
    // Compute the time between the previous end and current start, accounting for day change
    currentStart.add(1, 'day');
    const diff = currentStart.diff(previousEnd, 'hours', true);
    if (diff < minHours) {
      return schedule1.day;
    }
  }
  return null;
}
export function maxTotalHours(maxHours: number): ValidatorFn {
  return (control: AbstractControl<Schedule[]>): ValidationErrors | null => {
    let schedules = control.value;
    if (!schedules) return null;
    schedules = schedules.filter((schedule) =>
      schedule.slots.some((slot) => slot.startTime && slot.endTime),
    );
    const totalHours = schedules.reduce((total, schedule) => {
      const dayHours = schedule.slots
        .filter((slot) => slot.startTime && slot.endTime)
        .filter((slot) => {
          // Filter out slots with invalid time formats
          const start = moment(slot.startTime, 'HH:mm', true);
          const end = moment(slot.endTime, 'HH:mm', true);
          return start.isValid() && end.isValid();
        })
        .reduce((dayTotal, slot) => {
          const start = moment(slot.startTime, 'HH:mm');
          const end = moment(slot.endTime, 'HH:mm');
          return dayTotal + end.diff(start, 'hours', true);
        }, 0);
      return total + dayHours;
    }, 0);
    if (totalHours > maxHours) {
      return { maxTotalHours: { actual: totalHours, max: maxHours } };
    }
    return null;
  };
}

export function minTotalHours(expectedHours: number): ValidatorFn {
  return (control: AbstractControl<Schedule[]>): ValidationErrors | null => {
    let schedules = control.value;
    if (!schedules) return null;
    schedules = schedules.filter((schedule) =>
      schedule.slots.some((slot) => slot.startTime && slot.endTime),
    );
    const totalHours = schedules.reduce((total, schedule) => {
      const dayHours = schedule.slots
        .filter((slot) => slot.startTime && slot.endTime)
        .filter((slot) => {
          // Filter out slots with invalid time formats
          const start = moment(slot.startTime, 'HH:mm', true);
          const end = moment(slot.endTime, 'HH:mm', true);
          return start.isValid() && end.isValid();
        })
        .reduce((dayTotal, slot) => {
          const start = moment(slot.startTime, 'HH:mm');
          const end = moment(slot.endTime, 'HH:mm');
          return dayTotal + end.diff(start, 'hours', true);
        }, 0);
      return total + dayHours;
    }, 0);
    if (totalHours < expectedHours) {
      return { minTotalHours: { actual: totalHours, min: expectedHours } };
    }
    return null;
  };
}
